import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import {
  LogOut, User, FileText, Leaf, Receipt, CreditCard,
  CheckCircle, Download, Briefcase, Plus, X
} from 'lucide-react';
import toast from 'react-hot-toast';

// ─── Constants ────────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const FMT    = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const FMTN   = (n) => Number(n || 0).toLocaleString('en-IN');

const LEAVE_TYPES   = ['casual','sick','earned','maternity','paternity','unpaid','compensatory'];
const CLAIM_TYPES   = ['medical','travel','meal','mobile','internet','uniform','other'];
const CLAIM_COLORS  = {
  medical:'bg-red-50 text-red-700', travel:'bg-blue-50 text-blue-700',
  meal:'bg-amber-50 text-amber-700', mobile:'bg-purple-50 text-purple-700',
  internet:'bg-cyan-50 text-cyan-700', uniform:'bg-green-50 text-green-700',
  other:'bg-gray-50 text-gray-600'
};
const STATUS_COLORS = {
  pending :'bg-amber-50 text-amber-700',
  approved:'bg-blue-50 text-blue-700',
  rejected:'bg-red-50 text-red-600',
  paid    :'bg-green-50 text-green-700',
  active  :'bg-green-50 text-green-700',
  closed  :'bg-gray-50 text-gray-500'
};
const LEAVE_STATUS_COLORS = {
  pending :'bg-amber-50 text-amber-700',
  approved:'bg-green-50 text-green-700',
  rejected:'bg-red-50 text-red-600'
};

const Badge = ({ status, label, colorMap = STATUS_COLORS }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${colorMap[status] || 'bg-gray-50 text-gray-500'}`}>
    {label || status}
  </span>
);

// ─── Shared Modal Wrapper ─────────────────
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-4">
    <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
  >
    {children}
  </select>
);

const SubmitBtn = ({ loading, label = 'Submit' }) => (
  <button type="submit" disabled={loading}
    className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
    {loading ? 'Submitting...' : label}
  </button>
);

// ─── TAB: Home / Dashboard ─────────────────
const HomeTab = ({ data }) => {
  const statusColor = (s) =>
    s === 'selected'  ? 'bg-green-50 text-green-700' :
    s === 'rejected'  ? 'bg-red-50 text-red-600'     :
    s === 'interview' ? 'bg-blue-50 text-blue-700'   :
    s === 'offer'     ? 'bg-purple-50 text-purple-700':
    'bg-amber-50 text-amber-700';

  return (
    <div className="space-y-4">
      {/* Profile Card */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-blue-600 text-xl font-bold">
              {data?.candidate?.name?.charAt(0)}
            </span>
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-gray-900">{data?.candidate?.name}</h2>
            <p className="text-xs text-gray-400">{data?.candidate?.employee_code}</p>
            <p className="text-xs text-gray-400">{data?.candidate?.phone} · {data?.candidate?.city}</p>
          </div>
          <Badge status={data?.candidate?.status} colorMap={{
            deployed:'bg-green-50 text-green-700', active:'bg-blue-50 text-blue-700'
          }} />
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Profile Completion</span>
            <span className="font-medium">{data?.profile_completion}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5">
            <div className="bg-blue-600 h-1.5 rounded-full transition-all"
              style={{ width: `${data?.profile_completion}%` }} />
          </div>
        </div>
      </div>

      {/* Current Deployment */}
      {data?.current_deployment && (
        <div className="bg-green-50 border border-green-100 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={16} className="text-green-600" />
            <p className="text-xs font-semibold text-green-800">Currently Deployed</p>
          </div>
          <p className="text-base font-semibold text-gray-900">
            {data.current_deployment.clients?.company_name}
          </p>
          <p className="text-sm text-gray-500 mt-0.5">
            {data.current_deployment.designation} · {data.current_deployment.location}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            Since {data.current_deployment.start_date?.slice(0, 10)}
          </p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Applications', value: data?.applications?.length || 0, icon: Briefcase, color: 'text-blue-600' },
          { label: 'Documents',    value: data?.documents_uploaded || 0,   icon: FileText,  color: 'text-green-600' },
          { label: 'Profile',      value: `${data?.profile_completion || 0}%`, icon: User, color: 'text-purple-600' }
        ].map(item => (
          <div key={item.label} className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <item.icon size={18} className={`mx-auto mb-1.5 ${item.color}`} />
            <p className="text-lg font-semibold text-gray-900">{item.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{item.label}</p>
          </div>
        ))}
      </div>

      {/* Applications */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">My Applications</h3>
        {!data?.applications?.length ? (
          <div className="text-center py-6">
            <Briefcase size={24} className="mx-auto text-gray-200 mb-2" />
            <p className="text-sm text-gray-400">No applications yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.applications.map(app => (
              <div key={app.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-gray-900">{app.job_requisitions?.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{app.job_requisitions?.location}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(app.stage)}`}>
                  {app.stage}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── TAB: Payslips ─────────────────────────
const PayslipsTab = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [dlLoading, setDlLoading] = useState(null);

  useEffect(() => {
    API.get('/candidate-portal/payslips')
      .then(r => setPayslips(r.data.data || []))
      .catch(() => toast.error('Failed to load payslips'))
      .finally(() => setLoading(false));
  }, []);

  const downloadPayslip = async (id, month, year) => {
    setDlLoading(id);
    try {
      const res  = await API.get(`/candidate-portal/payslips/${id}/download`, { responseType: 'blob' });
      const name = `Payslip_${MONTHS[month - 1]}_${year}.pdf`;
      const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a    = document.createElement('a'); a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${name} downloaded`);
    } catch { toast.error('Download failed'); }
    finally   { setDlLoading(null); }
  };

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Loading payslips...</div>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-400">Your processed payslips. Tap Download to get a PDF copy.</p>
      {!payslips.length ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <FileText size={28} className="mx-auto text-gray-200 mb-3" />
          <p className="text-sm text-gray-400">No payslips available yet</p>
          <p className="text-xs text-gray-300 mt-1">Payslips appear once your payroll is processed</p>
        </div>
      ) : (
        payslips.map(p => (
          <div key={p.id} className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {MONTHS[p.month - 1]} {p.year}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{p.clients?.company_name} · #{p.payroll_no}</p>
              </div>
              <div className="text-right">
                <p className="text-base font-bold text-green-700">₹{FMTN(Math.round(Number(p.net_salary)))}</p>
                <p className="text-xs text-gray-400">Gross ₹{FMTN(Math.round(Number(p.gross_salary)))}</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <Badge status={p.status} />
              <button
                onClick={() => downloadPayslip(p.id, p.month, p.year)}
                disabled={dlLoading === p.id}
                className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50">
                <Download size={13} />
                {dlLoading === p.id ? 'Downloading...' : 'Download PDF'}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// ─── TAB: Leave ────────────────────────────
const LeaveTab = () => {
  const [balances,  setBalances]  = useState([]);
  const [requests,  setRequests]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    leave_type: 'casual', from_date: '', to_date: '', days: '', reason: ''
  });

  const year = new Date().getFullYear();

  const load = () => {
    Promise.all([
      API.get(`/candidate-portal/leave-balance?year=${year}`),
      API.get('/candidate-portal/leave-requests')
    ]).then(([b, r]) => {
      setBalances(b.data.data || []);
      setRequests(r.data.data || []);
    }).catch(() => toast.error('Failed to load leave data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Auto-calculate days from dates
  const calcDays = (from, to) => {
    if (!from || !to) return '';
    const diff = (new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24);
    return diff >= 0 ? (diff + 1).toString() : '';
  };

  const handleDateChange = (field, val) => {
    const updated = { ...form, [field]: val };
    updated.days  = calcDays(updated.from_date, updated.to_date);
    setForm(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.from_date || !form.to_date || !form.days) {
      return toast.error('Please fill all required fields');
    }
    setSubmitting(true);
    try {
      await API.post('/candidate-portal/leave-requests', form);
      toast.success('Leave request submitted');
      setShowForm(false);
      setForm({ leave_type: 'casual', from_date: '', to_date: '', days: '', reason: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Loading leave data...</div>;

  return (
    <div className="space-y-4">
      {/* Leave Balance Tiles */}
      {balances.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {balances.map(b => (
            <div key={b.leave_type} className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-400 capitalize">{b.leave_type.replace('_', ' ')} Leave</p>
              <div className="flex items-end gap-1 mt-1">
                <span className="text-2xl font-bold text-blue-600">{Number(b.balance || 0)}</span>
                <span className="text-xs text-gray-400 mb-0.5">/ {Number(b.allocated || 0)} days</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1 mt-2">
                <div className="bg-blue-500 h-1 rounded-full"
                  style={{ width: `${Math.min(100, (Number(b.used) / Number(b.allocated)) * 100)}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">{Number(b.used || 0)} used</p>
            </div>
          ))}
        </div>
      )}

      {balances.length === 0 && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-sm text-amber-700">
          Leave balances not set up yet. Contact your manager.
        </div>
      )}

      {/* Apply Leave */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">Leave Requests</p>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-700">
          <Plus size={13} /> Apply Leave
        </button>
      </div>

      {/* Requests List */}
      {!requests.length ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
          <Leaf size={24} className="mx-auto text-gray-200 mb-2" />
          <p className="text-sm text-gray-400">No leave requests yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {requests.map(r => (
            <div key={r.id} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 capitalize">
                    {r.leave_type.replace('_', ' ')} Leave
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {r.from_date?.slice(0, 10)} → {r.to_date?.slice(0, 10)} · {Number(r.days)} day{Number(r.days) !== 1 ? 's' : ''}
                  </p>
                  {r.reason && <p className="text-xs text-gray-500 mt-0.5">{r.reason}</p>}
                  {r.review_notes && <p className="text-xs text-red-500 mt-0.5">Note: {r.review_notes}</p>}
                </div>
                <Badge status={r.status} colorMap={LEAVE_STATUS_COLORS} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Apply Leave Modal */}
      {showForm && (
        <Modal title="Apply for Leave" onClose={() => setShowForm(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label="Leave Type *">
              <Select value={form.leave_type} onChange={e => setForm({ ...form, leave_type: e.target.value })}>
                {LEAVE_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="From Date *">
                <Input type="date" value={form.from_date} required
                  onChange={e => handleDateChange('from_date', e.target.value)} />
              </Field>
              <Field label="To Date *">
                <Input type="date" value={form.to_date} required
                  onChange={e => handleDateChange('to_date', e.target.value)} />
              </Field>
            </div>
            <Field label="Number of Days">
              <Input type="number" step="0.5" min="0.5" value={form.days}
                onChange={e => setForm({ ...form, days: e.target.value })}
                placeholder="Auto-calculated" />
            </Field>
            <Field label="Reason">
              <textarea value={form.reason} rows={2}
                onChange={e => setForm({ ...form, reason: e.target.value })}
                placeholder="Optional reason..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </Field>
            <SubmitBtn loading={submitting} label="Submit Leave Request" />
          </form>
        </Modal>
      )}
    </div>
  );
};

// ─── TAB: Reimbursements ───────────────────
const ReimbursementsTab = () => {
  const [list,      setList]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    claim_type: 'medical', amount: '', claim_date: '', description: '', bill_no: ''
  });

  const load = () => {
    API.get('/candidate-portal/reimbursements')
      .then(r => setList(r.data.data || []))
      .catch(() => toast.error('Failed to load reimbursements'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await API.post('/candidate-portal/reimbursements', form);
      toast.success('Claim submitted successfully');
      setShowForm(false);
      setForm({ claim_type: 'medical', amount: '', claim_date: '', description: '', bill_no: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  const total   = list.reduce((s, r) => s + Number(r.amount || 0), 0);
  const pending = list.filter(r => r.status === 'pending').length;
  const paid    = list.filter(r => r.status === 'paid').reduce((s, r) => s + Number(r.amount || 0), 0);

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Loading...</div>;

  return (
    <div className="space-y-4">
      {/* Summary tiles */}
      {list.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total Claims',  value: `₹${FMTN(Math.round(total))}`, c: 'text-gray-900' },
            { label: 'Pending',       value: pending,                         c: 'text-amber-600' },
            { label: 'Paid Amount',   value: `₹${FMTN(Math.round(paid))}`,  c: 'text-green-700' }
          ].map(({ label, value, c }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-100 p-3 text-center">
              <p className="text-xs text-gray-400">{label}</p>
              <p className={`text-base font-bold mt-0.5 ${c}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Header + Add */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">My Claims</p>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-700">
          <Plus size={13} /> New Claim
        </button>
      </div>

      {/* List */}
      {!list.length ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <Receipt size={24} className="mx-auto text-gray-200 mb-2" />
          <p className="text-sm text-gray-400">No reimbursement claims yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map(r => (
            <div key={r.id} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${CLAIM_COLORS[r.claim_type] || 'bg-gray-50 text-gray-500'}`}>
                      {r.claim_type}
                    </span>
                    {r.bill_no && <span className="text-xs text-gray-400">Bill: {r.bill_no}</span>}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {r.claim_date?.slice(0, 10)} · {r.deployments?.clients?.company_name || '—'}
                  </p>
                  {r.description && <p className="text-xs text-gray-400 mt-0.5">{r.description}</p>}
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <p className="text-base font-bold text-gray-900">₹{FMTN(Math.round(Number(r.amount)))}</p>
                  <Badge status={r.status} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Claim Modal */}
      {showForm && (
        <Modal title="Submit Reimbursement Claim" onClose={() => setShowForm(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label="Claim Type *">
              <Select value={form.claim_type} onChange={e => setForm({ ...form, claim_type: e.target.value })}>
                {CLAIM_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount (₹) *">
                <Input type="number" step="0.01" min="1" value={form.amount} required
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00" />
              </Field>
              <Field label="Claim Date *">
                <Input type="date" value={form.claim_date} required
                  onChange={e => setForm({ ...form, claim_date: e.target.value })} />
              </Field>
            </div>
            <Field label="Bill / Receipt No.">
              <Input type="text" value={form.bill_no}
                onChange={e => setForm({ ...form, bill_no: e.target.value })}
                placeholder="Optional bill number" />
            </Field>
            <Field label="Description">
              <textarea value={form.description} rows={2}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description of the expense..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </Field>
            <SubmitBtn loading={submitting} label="Submit Claim" />
          </form>
        </Modal>
      )}
    </div>
  );
};

// ─── TAB: Advances / Loans ─────────────────
const AdvancesTab = () => {
  const [list,      setList]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    loan_type: 'advance', amount: '', recovery_months: '3', reason: ''
  });

  const load = () => {
    API.get('/candidate-portal/advances')
      .then(r => setList(r.data.data || []))
      .catch(() => toast.error('Failed to load advances'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const monthlyDeduction = form.amount && form.recovery_months
    ? (Number(form.amount) / Number(form.recovery_months)).toFixed(2)
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await API.post('/candidate-portal/advances', form);
      toast.success('Request submitted successfully');
      setShowForm(false);
      setForm({ loan_type: 'advance', amount: '', recovery_months: '3', reason: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  const totalActive = list.filter(a => a.status === 'approved').reduce((s, a) => s + Number(a.amount || 0), 0);

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Loading...</div>;

  return (
    <div className="space-y-4">
      {list.length > 0 && totalActive > 0 && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-sm text-amber-700">
          You have ₹{FMTN(Math.round(totalActive))} in approved advances/loans pending recovery.
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">My Advances & Loans</p>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-700">
          <Plus size={13} /> Request
        </button>
      </div>

      {!list.length ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <CreditCard size={24} className="mx-auto text-gray-200 mb-2" />
          <p className="text-sm text-gray-400">No advance or loan requests yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map(a => (
            <div key={a.id} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      a.loan_type === 'loan' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                      {a.loan_type === 'loan' ? 'Loan' : 'Advance'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {a.deployments?.clients?.company_name} · {a.recovery_months} month{a.recovery_months !== 1 ? 's' : ''} recovery
                  </p>
                  {a.reason && <p className="text-xs text-gray-400 mt-0.5">{a.reason}</p>}
                  <p className="text-xs text-gray-300 mt-0.5">{a.created_at?.slice(0, 10)}</p>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <p className="text-base font-bold text-gray-900">₹{FMTN(Math.round(Number(a.amount)))}</p>
                  <p className="text-xs text-gray-400">
                    ₹{FMT(Number(a.amount) / (a.recovery_months || 1))}/mo
                  </p>
                  <div className="mt-1"><Badge status={a.status} /></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <Modal title="Request Advance / Loan" onClose={() => setShowForm(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label="Type">
              <Select value={form.loan_type} onChange={e => setForm({ ...form, loan_type: e.target.value })}>
                <option value="advance">Salary Advance (short-term)</option>
                <option value="loan">Loan (long-term)</option>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount (₹) *">
                <Input type="number" step="100" min="100" value={form.amount} required
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                  placeholder="0" />
              </Field>
              <Field label="Recovery Months *">
                <Select value={form.recovery_months} onChange={e => setForm({ ...form, recovery_months: e.target.value })}>
                  {[1,2,3,4,5,6,9,12].map(m => <option key={m} value={m}>{m} month{m !== 1 ? 's' : ''}</option>)}
                </Select>
              </Field>
            </div>
            {monthlyDeduction && (
              <div className="bg-blue-50 rounded-lg px-3 py-2 text-sm text-blue-700">
                Monthly deduction: <strong>₹{Number(monthlyDeduction).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
              </div>
            )}
            <Field label="Reason">
              <textarea value={form.reason} rows={2}
                onChange={e => setForm({ ...form, reason: e.target.value })}
                placeholder="Reason for this request..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </Field>
            <SubmitBtn loading={submitting} label="Submit Request" />
          </form>
        </Modal>
      )}
    </div>
  );
};

// ─── Main Portal ───────────────────────────
const TABS = [
  { id: 'home',      label: 'Home',      icon: User     },
  { id: 'payslips',  label: 'Payslips',  icon: FileText },
  { id: 'leave',     label: 'Leave',     icon: Leaf     },
  { id: 'reimb',     label: 'Claims',    icon: Receipt  },
  { id: 'advances',  label: 'Advances',  icon: CreditCard }
];

const CandidatePortal = () => {
  const navigate     = useNavigate();
  const [tab,        setTab]     = useState('home');
  const [dashData,   setDashData] = useState(null);
  const [loading,    setLoading]  = useState(true);

  const user = JSON.parse(localStorage.getItem('candidateUser') || '{}');

  useEffect(() => {
    API.get('/candidate-portal/status')
      .then(res => setDashData(res.data.data))
      .catch(err => {
        if (err.response?.status === 401) navigate('/candidate/login');
        else toast.error('Failed to load profile');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('candidateUser');
    navigate('/candidate/login');
    toast.success('Logged out');
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-400 text-sm">
      Loading your portal...
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">M</span>
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">My Portal</p>
              <p className="text-xs text-gray-400">{user.name}</p>
            </div>
          </div>
          <button onClick={handleLogout}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500">
            <LogOut size={16} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="bg-white border-b border-gray-100 sticky top-[57px] z-10">
        <div className="max-w-lg mx-auto flex overflow-x-auto scrollbar-hide">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex-shrink-0 flex flex-col items-center gap-0.5 px-4 py-2.5 text-xs font-medium transition border-b-2
                ${tab === id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto p-4 pb-10">
        {tab === 'home'     && <HomeTab data={dashData} />}
        {tab === 'payslips' && <PayslipsTab />}
        {tab === 'leave'    && <LeaveTab />}
        {tab === 'reimb'    && <ReimbursementsTab />}
        {tab === 'advances' && <AdvancesTab />}
      </div>
    </div>
  );
};

export default CandidatePortal;
